import type {
  StudioContentBlock,
  StudioPageContent,
  StudioPageSection,
} from "../model/page-content";
import type { PanderrSection, PanderrTemplatePage } from "./types";

function cleanText(value?: string): string {
  return String(value || "")
    .replace(/\s+/g, " ")
    .trim();
}

function blockText(block: StudioContentBlock): string[] {
  switch (block.type) {
    case "heading":
    case "paragraph":
    case "quote":
      return cleanText(block.text) ? [cleanText(block.text)] : [];

    case "list":
      return block.items.map(cleanText).filter(Boolean);

    case "link":
      return cleanText(block.link.text) ? [cleanText(block.link.text)] : [];

    case "image":
      return [];

    default:
      return [];
  }
}

function sectionText(section: StudioPageSection): string[] {
  return section.blocks.flatMap(blockText).filter(Boolean);
}

function sourceHeading(section: StudioPageSection): string {
  if (cleanText(section.heading)) {
    return cleanText(section.heading);
  }

  const heading = section.blocks.find((block) => block.type === "heading");

  return heading?.type === "heading" ? cleanText(heading.text) : "";
}

function sourceBody(section: StudioPageSection): string {
  const paragraphs = section.blocks
    .filter((block) => block.type === "paragraph")
    .map((block) => (block.type === "paragraph" ? cleanText(block.text) : ""))
    .filter(Boolean);

  if (paragraphs.length) {
    return paragraphs.join("\n\n");
  }

  return sectionText(section)
    .filter((text) => text !== sourceHeading(section))
    .join("\n\n");
}

function meaningfulSections(content?: StudioPageContent): StudioPageSection[] {
  if (!content) return [];

  return content.sections.filter((section) => {
    return Boolean(
      sourceHeading(section) ||
      sourceBody(section) ||
      section.blocks.some((block) => block.type === "image"),
    );
  });
}

function adaptSection(
  templateSection: PanderrSection,
  sourceSection?: StudioPageSection,
): PanderrSection {
  if (!sourceSection) {
    return { ...templateSection };
  }

  const heading = sourceHeading(sourceSection);
  const body = sourceBody(sourceSection);

  return {
    ...templateSection,

    // Keep template presentation, but prefer recovered source copy.
    heading: heading || templateSection.heading,
    body: body || templateSection.body,
  };
}

/**
 * Maps canonical source-derived page content into a design-system page.
 *
 * StudioPage.content remains the canonical recovered content. This function
 * creates presentation data only and never mutates either input.
 *
 * V1 deliberately uses ordered best-effort mapping. More specialized
 * semantic matching can be layered on later without changing the renderer.
 */
export function adaptTemplatePageContent(
  templatePage: PanderrTemplatePage,
  content?: StudioPageContent,
): PanderrTemplatePage {
  const sourceSections = meaningfulSections(content);

  if (!sourceSections.length) {
    return {
      ...templatePage,
      sections: templatePage.sections.map((section) => ({ ...section })),
    };
  }

  return {
    ...templatePage,
    sections: templatePage.sections.map((section, index) =>
      adaptSection(section, sourceSections[index]),
    ),
  };
}
